import Loader from "@/components/core/layouts/Loader";
import { CasUrlResponseDto } from "@/state/auth/dto/response/cas.url.response.dto";
import PageLayout from "@components/core/layouts/PageLayout";
import { ROUTES } from "@routes/Routes";
import { officeService } from "@services/officeService";
import {
  requestCasURL,
  requestGetUserInfo,
  requestLogin,
} from "@state/auth/AuthEffects";
import { setUser } from "@state/auth/AuthEvents";
import { authenticationStore } from "@state/auth/AuthStore";
import IconAlert from "@static/core/icons/icon-alert.svg?react";
import { AuthResult, AuthenticationContext } from "@type/core/auth/auth.types";
import { CALLBACK_URL_PARAM, ECMA_DELEGATION_INFO_LINK } from "@utils/Constant";
import { ParsedResponse } from "@utils/rest/ServerResponseParse";
import { useUnit } from "effector-react";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

const NO_OFFICES_TRANSLATION_KEY = "login.messages.noOffices.message";

const LoginScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const callback = useRef<string>(
    new URLSearchParams(location.search).get("c"),
  );

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const user = authStore?.user;

  const [title, setTitle] = useState<string>(t("common.redirect.title"));
  const [message, setMessage] = useState<string>();
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [connecting, setConnecting] = useState<boolean>(false);

  useEffect(() => {
    const handleAuthentication = () => {
      void requestGetUserInfo({})
        .then((ar) => {
          if (ar.ok && ar.data) {
            setUser(ar.data);
            navigate(callback.current ?? ROUTES.extranet.home.generate());
          }
        })
        .finally(() => setConnecting(false));
    };

    const ticket = new URLSearchParams(location.search).get("ticket");
    const origin = `${window.location.origin}${window.location.pathname}?${CALLBACK_URL_PARAM}=${callback.current}`;
    if (user) {
      let alert = false;
      if (officeService.getAccessibleOffices(user).length === 0) {
        alert = true;
        setTitle(t("login.messages.noOffices.title"));
        setMessage(NO_OFFICES_TRANSLATION_KEY);
      } else {
        navigate(callback.current ?? ROUTES.extranet.home.generate());
      }
      setShowAlert(alert);
    } else if (ticket) {
      setConnecting(true);
      requestLogin({
        dto: {
          ticket: ticket,
          origin: origin,
        },
      })
        .then((authResponse: ParsedResponse<AuthResult>) => {
          if (authResponse.responseCode === 200) {
            handleAuthentication();
          } else {
            setConnecting(false);
            setTitle(t("login.messages.error.title"));
            if (authResponse.errorCode) {
              if (
                authResponse.errorCode === "1005" &&
                authResponse.maxAttempts &&
                authResponse.attempts
              ) {
                setMessage(
                  t(`login.messages.errors.${authResponse.errorCode}`, {
                    remainingAccessAttempts:
                      authResponse.maxAttempts - authResponse.attempts,
                  }),
                );
              } else {
                setMessage(
                  t(`login.messages.errors.${authResponse.errorCode}`),
                );
              }
            } else {
              setMessage(t("login.messages.error.message"));
            }
          }
        })
        .catch(() => {
          setConnecting(false);
          setTitle(t("login.messages.error.title"));
          setMessage(t("login.messages.error.message"));
          return false;
        });
    } else {
      requestCasURL({
        dto: {
          origin: origin,
        },
      })
        .then((casResponse: ParsedResponse<CasUrlResponseDto>) => {
          if (casResponse.ok && casResponse.data)
            window.location.href = casResponse.data.url;
          return true;
        })
        .catch(() => {
          setMessage(t("login.messages.error.message"));
          return false;
        });
    }
  }, [callback, navigate, user, location, t]);

  const loader = connecting ? (
    <Loader className="my-3" />
  ) : (
    <div className="my-1" />
  );
  return (
    <PageLayout
      title={
        message === NO_OFFICES_TRANSLATION_KEY ? undefined : t("login.title")
      }
      bodyClassName="app-error-screen pt-5 d-flex flex-column flex-xl-row align-items-center justify-content-center"
    >
      <div
        className={`error-text-container d-flex flex-column align-items-center justify-content-${message ? "start" : "center"} ${message && "bg-white"}`}
      >
        <h3
          className={`text-primary text-${message ? "left" : "center"} w-100`}
        >
          {title}
        </h3>
        {showAlert ? (
          <div className="Icon">
            <IconAlert />
          </div>
        ) : (
          loader
        )}
        {message && (
          <p className="text-danger w-100 text-left">
            {message === NO_OFFICES_TRANSLATION_KEY ? (
              <Trans
                i18nKey={message}
                values={{ website: "Jesignexpert.com", delegationLink: "ici" }}
                components={[
                  // eslint-disable-next-line react/jsx-key
                  <u></u>,
                  // eslint-disable-next-line react/jsx-key
                  <a
                    className="text-danger"
                    href={ECMA_DELEGATION_INFO_LINK}
                    target="_blank"
                    rel="noreferrer noopener"
                  />,
                ]}
              />
            ) : (
              message
            )}
          </p>
        )}
      </div>
    </PageLayout>
  );
};

export default LoginScreen;
